// Session + principal [02 §7-8; MB D3; CR-W2a-38]. The API serves Better Auth under /api/v1/auth/* (outside the house
// envelope): e-mail + password, then the TOTP code, then the principal from GET /api/v1/users/me.
//   POST /v1/auth/sign-in/email          → `{ twoFactorRedirect: true }` (code needed) | a session
//   POST /v1/auth/two-factor/verify-totp → session cookie
//   GET  /v1/users/me                    → userId, displayName, locale, permissions, landing (resolved server-side)
//   POST /v1/auth/step-up                → house envelope (sensitive actions, STEP_UP_REQUIRED)
//   POST /v1/auth/sign-out
// Invite and re-enrolment (CR-W2a-42 Q4):
//   POST /v1/auth/invites/accept         → house envelope { userId, otpauthUrl, backupCodes } (token + password in
//                                          the body; the token reaches the page only in the URL fragment)
//   POST /v1/auth/two-factor/enable      → { totpURI, backupCodes } for a password-only session after an MFA reset
import { isPermissionKey, type ErrorBody, type PermissionKey } from '@skm/core';
import { z } from 'zod';
import { ApiError, type ApiClient } from '../api/client.ts';

const SIGN_IN = '/v1/auth/sign-in/email';
const VERIFY_TOTP = '/v1/auth/two-factor/verify-totp';
const STEP_UP = '/v1/auth/step-up';
const SIGN_OUT = '/v1/auth/sign-out';
const ME = '/v1/users/me';
const ACCEPT_INVITE = '/v1/auth/invites/accept';
const ENABLE_TOTP = '/v1/auth/two-factor/enable';

/** Web route of the invite link (`<APP_BASE_URL>/accept-invite#token=…`, apps/api rbac/users.ts). */
export const ACCEPT_INVITE_PATH = '/accept-invite';

/** Message key of "the code challenge expired": the UI goes back to the password step on it. */
export const MFA_EXPIRED_KEY = 'frame.mfa.expired';

/** `GET /users/me` as the web reads it (the API sends more: screens, sensitive views, feature flags). */
const meSchema = z.object({
  userId: z.string(),
  kind: z.literal('user'),
  displayName: z.string().nullable(),
  locale: z.string().nullable(),
  permissions: z.array(z.string()),
  /** Landing screen id: user pick > primary role > dashboard, already checked against the permissions. */
  landing: z.string(),
  /** Enabled feature flags (CR-W2e-12, R-NLA-3): a flagged screen such as `reports.ask` shows only when its flag is here. */
  featureFlags: z.array(z.string()).default([]),
});
export type PrincipalDto = z.input<typeof meSchema>;

export interface Principal {
  readonly userId: string;
  readonly displayName: string;
  readonly locale: string | null;
  readonly permissions: ReadonlySet<PermissionKey>;
  /** Landing screen id [BR-PERM-003]. */
  readonly landing: string;
  /** Enabled feature flags from `/users/me`; absent = none (flagged screens stay hidden). */
  readonly featureFlags?: ReadonlySet<string>;
}

/**
 * `mfa`: the account needs its TOTP code. `enrol`: the password opened a session for an account whose authenticator
 * was reset by an administrator; it must enrol a new one before the API accepts it (CR-W2a-38, CR-W2a-42 Q4).
 */
export type SignInResult =
  | { readonly status: 'ok'; readonly principal: Principal }
  | { readonly status: 'mfa' }
  | { readonly status: 'enrol' };

/** What the user copies into the authenticator app, shown once. */
export interface Enrolment {
  readonly otpauthUrl: string;
  readonly backupCodes: readonly string[];
}

/** Invite token from `location.hash` (`#token=…`, base64url as minted by the API); null when absent or malformed. */
export function inviteTokenFromHash(hash: string): string | null {
  if (!hash.startsWith('#')) return null;
  const raw = new URLSearchParams(hash.slice(1)).get('token');
  return raw !== null && /^[A-Za-z0-9_-]{20,200}$/.test(raw) ? raw : null;
}

/** Manual-entry key (grouped by four) and account label from an otpauth key-URI; null if it is not one. */
export function keyFromOtpauth(url: string): { key: string; account: string } | null {
  if (!url.startsWith('otpauth://totp/')) return null;
  try {
    const u = new URL(url);
    const secret = u.searchParams.get('secret');
    if (!secret) return null;
    return {
      key: secret.replace(/(.{4})(?=.)/g, '$1 '),
      account: decodeURIComponent(u.pathname.replace(/^\/+/, '')),
    };
  } catch {
    return null;
  }
}

export function toPrincipal(dto: PrincipalDto): Principal {
  const me = meSchema.parse(dto);
  return {
    userId: me.userId,
    displayName: me.displayName ?? '',
    locale: me.locale,
    landing: me.landing,
    permissions: new Set(me.permissions.filter(isPermissionKey)),
    featureFlags: new Set(me.featureFlags),
  };
}

/** Better Auth error code → house error (code + message key the UI already translates). */
const VENDOR_ERRORS: Readonly<Record<string, Pick<ErrorBody, 'code' | 'messageKey'>>> = {
  INVALID_CODE: { code: 'UNAUTHENTICATED', messageKey: 'auth.errors.code_invalid' },
  INVALID_TWO_FACTOR_COOKIE: { code: 'UNAUTHENTICATED', messageKey: MFA_EXPIRED_KEY },
  ACCOUNT_TEMPORARILY_LOCKED: { code: 'RATE_LIMITED', messageKey: 'frame.mfa.locked' },
  TOTP_NOT_ENABLED: { code: 'MFA_REQUIRED', messageKey: 'errors.code.MFA_REQUIRED' },
  TWO_FACTOR_NOT_ENABLED: { code: 'MFA_REQUIRED', messageKey: 'errors.code.MFA_REQUIRED' },
};

/** Without a known vendor code the status decides: slow down, sign in again, or something broke. */
function byStatus(status: number): Pick<ErrorBody, 'code' | 'messageKey'> {
  if (status === 429) return { code: 'RATE_LIMITED', messageKey: 'errors.code.RATE_LIMITED' };
  if (status === 401) return { code: 'UNAUTHENTICATED', messageKey: 'errors.code.UNAUTHENTICATED' };
  return { code: 'INTERNAL', messageKey: 'errors.code.INTERNAL' };
}

function vendorCode(e: ApiError): string | null {
  const vendor = (e.body.details as { vendor?: { code?: unknown } } | undefined)?.vendor;
  return typeof vendor?.code === 'string' ? vendor.code : null;
}

/** A Better Auth failure becomes a house `ApiError`; a house envelope (sign-in refusal, lock-out) is kept as is. */
function toAuthError(e: unknown): unknown {
  if (!(e instanceof ApiError) || !e.body.details || !('vendor' in e.body.details)) return e;
  const code = vendorCode(e);
  const mapped = (code && VENDOR_ERRORS[code]) || byStatus(e.status);
  return new ApiError(e.status, { ...mapped, message: '' });
}

export function createAuth(api: ApiClient) {
  const raw = async <T>(path: string, body: unknown): Promise<T> => {
    try {
      return await api.create<T>(path, body, { raw: true });
    } catch (e) {
      throw toAuthError(e);
    }
  };
  const loadPrincipal = async (): Promise<Principal> =>
    toPrincipal(await api.get<PrincipalDto>(ME));
  return {
    /** The signed-in principal, or null when there is no session (401). */
    async me(): Promise<Principal | null> {
      try {
        return await loadPrincipal();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    /** Password step: `mfa` when the account needs its TOTP code, `ok` if the API opened a session at once. */
    async signIn(email: string, password: string): Promise<SignInResult> {
      const r = await raw<{
        twoFactorRedirect?: boolean;
        user?: { twoFactorEnabled?: boolean | null };
      } | null>(SIGN_IN, { email, password });
      if (r?.twoFactorRedirect) return { status: 'mfa' };
      if (r?.user?.twoFactorEnabled === false) return { status: 'enrol' };
      return { status: 'ok', principal: await loadPrincipal() };
    },
    /** Sets the password with the one-use invite token; the answer (key-URI + recovery codes) is shown once. */
    async acceptInvite(token: string, password: string): Promise<Enrolment> {
      const r = await api.create<{ otpauthUrl: string; backupCodes: string[] }>(ACCEPT_INVITE, {
        token,
        password,
      });
      return { otpauthUrl: r.otpauthUrl, backupCodes: r.backupCodes };
    },
    /** Re-enrolment after an MFA reset: a new key for the authenticator app; `verifyTotp` then completes it. */
    async startEnrol(password: string): Promise<Enrolment> {
      const r = await raw<{ totpURI?: unknown; backupCodes?: unknown } | null>(ENABLE_TOTP, {
        password,
      });
      if (typeof r?.totpURI !== 'string' || !r.totpURI.startsWith('otpauth://'))
        throw new ApiError(502, {
          code: 'INTERNAL',
          messageKey: 'errors.code.INTERNAL',
          message: '',
        });
      const codes = Array.isArray(r.backupCodes) ? r.backupCodes.map(String) : [];
      return { otpauthUrl: r.totpURI, backupCodes: codes };
    },
    async verifyTotp(code: string): Promise<Principal> {
      await raw(VERIFY_TOTP, { code });
      return loadPrincipal();
    },
    /** Step-up before sensitive actions (STEP_UP_REQUIRED). */
    async stepUp(code: string): Promise<void> {
      await api.create(STEP_UP, { code });
    },
    async signOut(): Promise<void> {
      await raw(SIGN_OUT, {});
    },
  };
}
export type Auth = ReturnType<typeof createAuth>;
